import {
  Box,
  Button,
  CircularProgress,
  FormControl,
  IconButton,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { StatusItem } from "@types";
import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from "react";
import {
  getInspStatusImage,
  getNGLabels,
  updateLabelsByThreshold,
  updateReviewLabel,
} from "../../../services/inspectService";
import ZoomBox from "../../train/ZoomBox";
import { KeyboardArrowUpIcon, KeyboardArrowDownIcon, ImageIcon, SearchIcon, truncateString } from "common-lib";
import { toast } from "react-toastify";
import { formatDate, SF } from "../../../utils/utils";
import { useTutorial } from "../../../context/TutorialContext";
import { TUTORIALS } from "../../../tutorial";
import { OpacityControllBox, DetectStrengthController } from "common_ui_lib";
import { t } from "i18next";
import { useStatusContext } from "../../../context/InspStatusContext";
import { usePopupWindow } from "../../../hooks/usePopupWindow";
import { getMyModuleServer, clearImgData, getImgData, sendImgDataFromStorage, updateAIModules } from "common-lib";
import { aiModuleType } from "../../../recoil/upgrade";
import { ImportedModelItem } from "@types";
import { ScoremapOverlayProps } from "../ImagePart";
import ScoremapOverlay from "../../train/ScoremapOverlay";
import { clickSegment, LabeledSegment, prepareImage, SegmentFeature } from "common-lib";
import { fromSegmentFeatures, segmentHit, toSegmentFeatures } from "../../../utils/segmentUtils";
import { toNaturalCoords } from "../../feature/contour/coords";
import { SplitView } from "../../SplitView";
import ReviewGrass from "./ReviewGrass";
import { MaximizeIcon } from "lucide-react";

interface ModalProps {
  form: StatusItem;
  setForm: Dispatch<SetStateAction<StatusItem>>;
  imageSrc: any | null;
  setImageSrc: (img: string | null) => void;
  variant: "modal" | "popup";
  onPrevious: () => void;
  onNext: () => void;
  onJump: (id: number, ascIndex: number, total: number) => void;

  openMakeDataSet: boolean;
  setOpenMakeDataSet: (open: boolean) => void;
  importedModel: ImportedModelItem | null;
  reviewOptions: string[];
  onPopup?: () => void;
}

// review 컬럼 값: {"label_seg":[...], "label_cls":["NG"]} 또는 {"label_cls":["dent"]}
type ReviewValue = { label_seg?: SegmentFeature[]; label_cls: string[] };

// 기존 "OK"/"NG" 평문 값도 같은 형태로 읽는다
const parseReview = (raw?: string | null): ReviewValue | null => {
  if (!raw) return null;
  try {
    const v = JSON.parse(raw);
    if (Array.isArray(v?.label_cls)) return v;
  } catch {}
  return { label_cls: [raw] };
};

const categoryTypoStyle = {
  fontSize: "11px",
  fontWeight: 500,
  color: "var(--dk-text-3, #aeaeb2)",
  letterSpacing: "0.02em",
  textTransform: "uppercase",
  width: 80,
  flexShrink: 0,
  fontFamily: SF,
};

export default function StatusContent({
  form,
  setForm,
  imageSrc,
  setImageSrc,
  onPrevious,
  onNext,
  onJump,
  variant,
  openMakeDataSet,
  setOpenMakeDataSet,
  importedModel,
  reviewOptions,
  onPopup,
}: ModalProps) {
  const Overlay = ScoremapOverlay as React.FC<ScoremapOverlayProps>;
  const { selectedStatus, setSelectedStatus, page, setPage, imageList, setImageList, filters, totalDataCount } =
    useStatusContext();
  const [myAIModule, setMyAIModule] = useState<aiModuleType | null>(null);
  const [imageData, setImageData] = useState<any | null>(null);
  const [ngCount, setNgCount] = useState(0);
  const [opacity, setOpacity] = useState(0.5);
  const [showHeatmap, setShowHeatmap] = useState<boolean>(false);
  const [splitDir, setSplitDir] = useState<"row" | "column">("row");
  const [threshold, setThreshold] = useState<number>(0.65);
  const [siteOption, setSiteOption] = useState(null);
  const [scoreMapImage, setScoreMapImage] = useState<any | null>(null);

  const { run, nextSubStep, tutorialKey, stepIndex, stepIndexRef } = useTutorial();
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapMsg, setHeatmapMsg] = useState<string | null>(null);
  // 리뷰가 저장될 때마다 증가 -> 잔디 즉시 갱신
  const [reviewTick, setReviewTick] = useState(0);
  const rootRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    fetch("/config.json")
      .then((res) => res.json())
      .then((config) => setSiteOption(config.SITE_OPTION));
  }, []);

  useEffect(() => {
    setForm({ ...selectedStatus! });
    const fetchImgData = async () => {
      try {
        const imageResponse = await getInspStatusImage(selectedStatus!.path);
        if (imageResponse) {
          setImageSrc(imageResponse);
        }
      } catch (error) {
        console.log(error);
      }
    };
    fetchImgData();
  }, [selectedStatus]);

  const handleMakeDataSet = async () => {
    setOpenMakeDataSet(true);

    try {
      const response = await getNGLabels({
        ...filters,
      });
      console.log(response);
    } catch (error) {
      console.error("Error in handleMakeDataSet:", error);
    }
  };

  const saveReview = async (value: ReviewValue) => {
    const review = JSON.stringify(value);
    const res = await updateReviewLabel(form.uuid, review); // 실패 시 서비스에서 toast 후 undefined 반환
    if (!res) throw new Error("update review failed");
    setForm((prev) => ({ ...prev, review }));
    setReviewTick((v) => v + 1);
  };

  const handleReviewChange = async (cls: string): Promise<boolean> => {
    const current = parseReview(form.review);
    if (current?.label_cls[0] === cls && !current.label_seg?.length) return true;
    // 클래스 버튼은 {"label_cls":[cls]}만 저장하므로 기존 컨투어는 지워진다
    if (current?.label_seg?.length && !window.confirm(t("ContourOverwriteMsg"))) return false;

    try {
      await saveReview({ label_cls: [cls] });
      setSegments([]);

      if (run) {
        const currentSubStep = TUTORIALS[tutorialKey][stepIndexRef.current];

        let tempNgCount = ngCount;

        // NG_list의 모든 클래스는 NG로 취급
        const wasNG = !!current && current.label_cls[0] !== "OK";
        const isNG = cls !== "OK";

        if (!wasNG && isNG) {
          tempNgCount++;
        } else if (wasNG && !isNG) {
          tempNgCount--;
        }
        setNgCount(tempNgCount);

        if (tempNgCount >= 5 && currentSubStep?.nextTrigger === "#make-dataset-btn") {
          nextSubStep();
        }
      }
      return true;
    } catch (error) {
      toast.error(t("ReviewFailedMsg"));
      return false;
    }
  };

  // 숫자키 1..n = 리뷰 버튼 순서. 저장되면 바로 다음 이미지로 넘어간다
  const keyHandlerRef = useRef<(e: KeyboardEvent) => void>(() => {});
  keyHandlerRef.current = async (e: KeyboardEvent) => {
    if (e.ctrlKey || e.altKey || e.metaKey) return;
    const tag = (e.target as HTMLElement)?.tagName;
    if (["INPUT", "TEXTAREA", "SELECT"].includes(tag)) return;

    const idx = Number(e.key) - 1;
    if (!Number.isInteger(idx) || idx < 0 || idx >= reviewOptions.length) return;
    e.preventDefault();
    if (await handleReviewChange(reviewOptions[idx])) onNext();
  };

  useEffect(() => {
    // 팝업으로 띄우면 DOM이 팝업 문서에 있으므로 이벤트도 그쪽에서 받는다
    const doc = rootRef.current?.ownerDocument;
    if (!doc) return;
    const fn = (e: KeyboardEvent) => keyHandlerRef.current(e);
    doc.addEventListener("keydown", fn);
    return () => doc.removeEventListener("keydown", fn);
  }, []);

  // ── 컨투어(SAM) ──
  const ngClasses = reviewOptions.filter((v) => v !== "OK");
  const [contourMode, setContourMode] = useState(true);
  const [contourClass, setContourClass] = useState(ngClasses[0] ?? "NG");
  const [segments, setSegments] = useState<LabeledSegment[]>([]);
  const [segmenting, setSegmenting] = useState(false);
  const [naturalSize, setNaturalSize] = useState<{ w: number; h: number } | null>(null);
  const contourImgRef = useRef<HTMLImageElement>(null);
  const preparedRef = useRef<{ src: string; id: string } | null>(null);

  useEffect(() => {
    if (!ngClasses.includes(contourClass)) setContourClass(ngClasses[0] ?? "NG");
  }, [reviewOptions]);

  useEffect(() => {
    // 저장된 컨투어 불러오기 (geometry 없는 항목은 무시)
    const seg = parseReview(selectedStatus?.review)?.label_seg ?? [];
    setSegments(fromSegmentFeatures(seg.filter((f) => f?.geometry)));
  }, [selectedStatus]);

  const handleContourClick = async (e: React.MouseEvent) => {
    const pt = toNaturalCoords(e, contourImgRef.current);
    if (!pt || !imageSrc || segmenting) return;

    setSegmenting(true);
    try {
      // 이미지당 1회만 인코딩
      let prepared = preparedRef.current;
      if (!prepared || prepared.src !== imageSrc) {
        prepared = { src: imageSrc, id: await prepareImage(imageSrc) };
        preparedRef.current = prepared;
      }
      const res = await clickSegment(prepared.id, pt.x, pt.y);
      if (!res?.polygons?.length) return;
      setSegments((prev) => [
        ...prev,
        {
          id: crypto.randomUUID?.() ?? String(Date.now() + Math.random()),
          label: contourClass,
          polygons: res.polygons,
        },
      ]);
    } catch (error) {
      console.error(error);
      preparedRef.current = null;
    } finally {
      setSegmenting(false);
    }
  };

  const handleContourRemove = (e: React.MouseEvent) => {
    e.preventDefault();
    const pt = toNaturalCoords(e, contourImgRef.current);
    if (!pt) return;
    const hit = [...segments].reverse().find((s) => segmentHit(pt.x, pt.y, s.polygons));
    if (hit) setSegments((prev) => prev.filter((s) => s.id !== hit.id));
  };

  const handleContourSave = async () => {
    if (!segments.length) {
      toast(t("ContourEmptyMsg"));
      return;
    }
    try {
      // 컨투어 저장 시 label_cls는 항상 NG, 세부 클래스는 각 세그먼트의 label에
      await saveReview({ label_seg: toSegmentFeatures(segments), label_cls: ["NG"] });
      toast.success("컨투어 및 라벨이 저장되었습니다.");
    } catch (error) {
      toast.error(t("ReviewFailedMsg"));
    }
  };

  useEffect(() => {
    const channel = new BroadcastChannel("status_popup_channel");
    return () => channel.close();
  }, []);

  const handleThresholdChange = useCallback((v: number | null) => {
    console.log("threshold changed:", v);
    if (!v) return;
    setThreshold(v);
  }, []);

  const handleSave = async () => {
    if (!importedModel) return;

    const moduleData = await getMyModuleServer(`${importedModel.checksum}_web`, null);
    // 1. 현재 연결된 검사모델 데이터 copy
    //  2. 복제된 model의 option의 threshold 값 변경
    //  3. original, copy의 info에 변경된 threhold 기입
    console.log("연결된 검사모델", importedModel);
    console.log("연결된 모듈", moduleData);

    const result = await updateLabelsByThreshold({
      threshold,
      filters: {
        model_uuid: selectedStatus?.model_uuid,
      },
    });

    console.log("response", result);
  };

  const handleCheckHeatmap = (checked: boolean) => {
    setShowHeatmap(checked);
  };

  useEffect(() => {
    // siteOption(config.json) 로드 전이면 site: null로 모듈이 갱신되므로 대기
    if (!showHeatmap || !importedModel || !siteOption) return;

    let cancelled = false;
    (async () => {
      try {
        const specId = `${importedModel.checksum}_web`;
        const moduleOption = await getMyModuleServer(specId, null);
        if (cancelled) return;

        const updated = {
          specId,
          serverAddress: moduleOption.serverAddress,
          ...moduleOption.module,
        };
        // 모듈을 scoremap 모드로 갱신한 뒤에 이미지 요청이 나가도록 순서 보장
        await fetchUpdateModule(updated);
        if (cancelled) return;
        setMyAIModule(updated);
      } catch (error) {
        console.error(error);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [showHeatmap, importedModel, siteOption]);

  useEffect(() => {
    if (!showHeatmap || !selectedStatus || !myAIModule || !importedModel) return;

    let cancelled = false;
    setHeatmapLoading(true);
    setHeatmapMsg(null);
    setScoreMapImage(null);
    (async () => {
      try {
        const uuid = `${importedModel.checksum}_web`;
        const sendData = {
          uuid,
          path: selectedStatus.path,
          destination: {
            index1: "tag",
            index2: "image_bytes_original",
            index3: "scoremap",
          },
        };

        // AI 서버가 이미지를 못 찾으면(results: "unfound") 뒤의 getImgData는 빈 응답만 반복 조회한다
        const sendRes = await sendImgDataFromStorage(myAIModule.serverAddress, sendData);
        if (cancelled) return;
        if (Array.isArray(sendRes) && sendRes.some((r: any) => r?.results && r.results !== "exist")) {
          setHeatmapMsg(`AI 서버(${myAIModule.serverAddress})에 이 이미지가 없습니다\n${selectedStatus.path}`);
          return;
        }

        const aiImageRes = await getImgData(myAIModule.serverAddress, uuid, null, null);
        if (cancelled) return;

        // scoremap은 여러 장이 오고 화면에 쓰는 건 마지막 한 장이다(ImagePart와 동일).
        // 여러 장을 통째로 넘기면 그 중 한 장만 깨져도 합성이 전부 실패한다.
        const raw = aiImageRes.scoremap ?? aiImageRes.processImage;
        const maps = Array.isArray(raw) ? raw.slice(-1) : raw ? [raw] : [];
        if (!maps.length) console.warn("히트맵 응답에 scoremap이 없습니다", aiImageRes);
        setHeatmapMsg(maps.length ? null : (aiImageRes.message ?? "히트맵 데이터가 없습니다"));
        setScoreMapImage(maps.length ? maps : null);
        setImageData(aiImageRes.originImage ?? null);

        await clearImgData(myAIModule.serverAddress, uuid);
      } catch (error: any) {
        console.error(error);
        if (!cancelled) setHeatmapMsg(error?.response?.data?.error ?? error?.message ?? "히트맵을 불러오지 못했습니다");
      } finally {
        if (!cancelled) setHeatmapLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [selectedStatus, showHeatmap, myAIModule, importedModel]);

  const fetchUpdateModule = async (module: aiModuleType) => {
    try {
      const data = {
        train_id: importedModel?.checksum + `_web`,
        site: siteOption,
        type: "scoremap",
        total: 0,
        trained_count: 0,
        is_complete: false,
      };

      const updateResult = await updateAIModules(module!.serverAddress, data, module);
      if (!updateResult) {
        throw new Error("AI 모듈 업데이트에 실패했습니다.");
      }
    } catch (error: any) {
      console.error(error);
    }
  };

  const isPopup = variant === "popup";

  return (
    <>
      <SplitView
        defaultRightWidth={isPopup ? "65%" : "60%"}
        sx={{
          p: "20px",
          flex: 1,
          minHeight: 0,
        }}
        rightSx={{
          background: "var(--dk-surface, white)",
          borderRadius: "12px",
          border: "0.5px solid var(--dk-border, rgba(0,0,0,0.09))",
          boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 320,
          position: "relative",
          p: "12px",
          boxSizing: "border-box",
        }}
        left={
          <Box ref={rootRef} sx={{ flex: 1, height: "100%" }}>
            <Box
              sx={{
                height: "100%",
                background: "var(--dk-surface, white)",
                borderRadius: "12px",
                border: "0.5px solid var(--dk-border, rgba(0,0,0,0.09))",
                boxShadow: "0 1px 3px rgba(0,0,0,0.05)",
                overflowY: "auto",
              }}
            >
              {[
                {
                  label: t("Name"),
                  value: (
                    <Tooltip title={form.name} arrow>
                      <span>{truncateString(form.name, 20)}</span>
                    </Tooltip>
                  ),
                },
                { label: t("MaterialName"), value: form.material_name },
                { label: t("LOT"), value: form.lot },
                { label: t("CreateDate"), value: formatDate(form.create_at) },
                { label: t("Label"), value: form.label },
              ].map((item, idx) => (
                <Box
                  key={idx}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    px: "14px",
                    py: "10px",
                    borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))",
                  }}
                >
                  <Typography sx={categoryTypoStyle}>{item.label}</Typography>
                  <Typography
                    sx={{
                      fontSize: "13px",
                      fontWeight: 500,
                      color: "var(--dk-text, #1d1d1f)",
                      letterSpacing: "-0.01em",
                      fontFamily: SF,
                    }}
                  >
                    {item.value || "-"}
                  </Typography>
                </Box>
              ))}

              {/* Review row */}
              <Box
                id="status-review"
                sx={{
                  display: "flex",
                  alignItems: "center",
                  px: "14px",
                  py: "10px",
                  borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))",
                }}
              >
                <Typography sx={categoryTypoStyle}>{t("Review")}</Typography>
                <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                  {reviewOptions.map((v, i) => (
                    <Button
                      key={v}
                      size="small"
                      onClick={() => handleReviewChange(v)}
                      sx={{
                        height: 26,
                        px: "12px",
                        borderRadius: "7px",
                        fontSize: "12px",
                        fontWeight: 600,
                        textTransform: "none",
                        fontFamily: SF,
                        border: "0.5px solid",
                        ...(parseReview(form.review)?.label_cls[0] === v
                          ? v === "OK"
                            ? { background: "#dcfce7", borderColor: "#86efac", color: "#16a34a" }
                            : { background: "#fee2e2", borderColor: "#fca5a5", color: "#dc2626" }
                          : {
                              background: "var(--dk-surface-2, #f5f5f7)",
                              borderColor: "var(--dk-border, rgba(0,0,0,0.14))",
                              color: "var(--dk-text-2, #6e6e73)",
                            }),
                        "&:hover": { opacity: 0.85 },
                      }}
                    >
                      {i + 1}. {v}
                    </Button>
                  ))}
                </Box>
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "8px",
                  px: "14px",
                  py: "10px",
                  borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                  <Typography sx={categoryTypoStyle}>{t("Contour")}</Typography>
                  <Switch
                    checked={contourMode}
                    onChange={(_, checked) => setContourMode(checked)}
                    disabled={!imageSrc}
                    size="small"
                  />
                </Box>
                {contourMode && (
                  <>
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: "6px" }}>
                      {ngClasses.map((v) => (
                        <Button
                          key={v}
                          size="small"
                          onClick={() => setContourClass(v)}
                          sx={{
                            height: 24,
                            minWidth: 0,
                            px: "10px",
                            borderRadius: "7px",
                            fontSize: "12px",
                            fontWeight: 600,
                            textTransform: "none",
                            fontFamily: SF,
                            border: "0.5px solid",
                            ...(contourClass === v
                              ? { background: "#fee2e2", borderColor: "#fca5a5", color: "#dc2626" }
                              : {
                                  background: "var(--dk-surface-2, #f5f5f7)",
                                  borderColor: "var(--dk-border, rgba(0,0,0,0.14))",
                                  color: "var(--dk-text-2, #6e6e73)",
                                }),
                          }}
                        >
                          {v}
                        </Button>
                      ))}
                    </Box>
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: "8px" }}>
                      <Typography sx={{ fontSize: "11px", color: "var(--dk-text-3, #aeaeb2)", fontFamily: SF }}>
                        {t("ContourHelpMsg")}
                      </Typography>
                      <Button
                        size="small"
                        onClick={handleContourSave}
                        sx={{ height: 26, textTransform: "none", fontSize: "12px", fontFamily: SF, flexShrink: 0 }}
                      >
                        {t("Save")} ({segments.length})
                      </Button>
                    </Box>
                  </>
                )}
              </Box>

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  px: "14px",
                  py: "10px",
                  borderBottom: "0.5px solid var(--dk-border, rgba(0,0,0,0.06))",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Typography sx={categoryTypoStyle}>{t("Heatmap")}</Typography>
                  <Switch
                    checked={showHeatmap}
                    onChange={(_, checked) => handleCheckHeatmap(checked)}
                    sx={{
                      width: 38,
                      height: 22,
                      padding: 0,
                      "& .MuiSwitch-switchBase": {
                        padding: "3px",
                        "&.Mui-checked": {
                          transform: "translateX(16px)",
                          color: "#fff",
                          "& + .MuiSwitch-track": {
                            backgroundColor: "#30d158", // mac 스타일 그린
                            opacity: 1,
                          },
                        },
                      },
                      "& .MuiSwitch-thumb": {
                        width: 16,
                        height: 16,
                        boxShadow: "0 1px 2px rgba(0,0,0,0.2)",
                      },
                      "& .MuiSwitch-track": {
                        borderRadius: 11,
                        backgroundColor: "#e9e9eb",
                        opacity: 1,
                        transition: "background-color 0.2s ease",
                      },
                    }}
                  />
                </Box>

                {showHeatmap && (
                  <Box sx={{ mt: "8px" }}>
                    <OpacityControllBox opacity={opacity} setOpacity={setOpacity} subTitleViewer={false} text="" />
                  </Box>
                )}
              </Box>

              <ReviewGrass filters={filters} selectedId={selectedStatus?.id} reviewTick={reviewTick} onJump={onJump} />
            </Box>
          </Box>
        }
        right={
          <>
            <Box
              sx={{
                position: "absolute",
                top: 8,
                right: 8,
                zIndex: 4,
                display: "flex",
                alignItems: "center",
                gap: "6px",
              }}
            >
              {imageSrc && showHeatmap && (
                <ToggleButtonGroup
                  value={splitDir}
                  exclusive
                  size="small"
                  onChange={(_, v) => v && setSplitDir(v)}
                  sx={{
                    height: 30,
                    background: "var(--dk-surface, white)",
                    "& .MuiToggleButton-root": { py: "2px", px: "10px", fontSize: "12px" },
                  }}
                >
                  <ToggleButton value="row">{t("Horizontal", "가로")}</ToggleButton>
                  <ToggleButton value="column">{t("Vertical", "세로")}</ToggleButton>
                </ToggleButtonGroup>
              )}
              {onPopup && (
                <Tooltip title="크게 보기" arrow>
                  <IconButton
                    onClick={onPopup}
                    sx={{
                      width: 30,
                      height: 30,
                      borderRadius: "8px",
                      border: "0.5px solid var(--dk-border, rgba(0,0,0,0.14))",
                      background: "var(--dk-surface, white)",
                      boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                      "&:hover": { background: "var(--dk-surface-2, #f5f5f7)" },
                    }}
                  >
                    <MaximizeIcon sx={{ fontSize: 16, color: "text.secondary" }} />
                  </IconButton>
                </Tooltip>
              )}
            </Box>

            {!imageSrc ? (
              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "center",
                  gap: "8px",
                }}
              >
                <Box sx={categoryTypoStyle}>
                  <ImageIcon size={18} color="#aeaeb2" />
                </Box>
                <Typography sx={{ fontSize: "12px", color: "var(--dk-text-3, #aeaeb2)", fontFamily: SF }}>
                  {t("NoImage")}
                </Typography>
              </Box>
            ) : (
              <Box
                sx={{
                  position: "relative",
                  flex: 1,
                  minHeight: 0,
                  width: "100%",
                  height: "100%",
                  overflow: "hidden",
                  // 히트맵 칸은 내용이 없어도 자리를 차지해야 하므로 트랙을 고정한다 (flex는 내용에 따라 접힌다)
                  display: "grid",
                  gridTemplateColumns: showHeatmap && splitDir === "row" ? "1fr 1fr" : "1fr",
                  gridTemplateRows: showHeatmap && splitDir === "column" ? "1fr 1fr" : "1fr",
                  gap: "8px",
                }}
              >
                {/* 히트맵 ON이면 원본 | 히트맵 나란히 (splitDir: 가로/세로) */}
                <Box sx={{ gridArea: "1 / 1", position: "relative", minWidth: 0, minHeight: 0, overflow: "hidden" }}>
                  <ZoomBox src={imageSrc} zoom={2.5} lensSize={180} zoomMode={false} />
                </Box>

                {showHeatmap && (
                  <Box
                    sx={{
                      gridArea: splitDir === "row" ? "1 / 2" : "2 / 1",
                      position: "relative",
                      minWidth: 0,
                      minHeight: 0,
                      overflow: "hidden",
                      borderRadius: "8px",
                      // 비어 있을 때도 칸이 보이게
                      border: scoreMapImage && imageData ? "none" : "1px dashed var(--dk-border, rgba(0,0,0,0.18))",
                    }}
                  >
                    {scoreMapImage && imageData && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                        }}
                      >
                        <Overlay
                          scoremapImages={scoreMapImage}
                          baseImage={imageData}
                          blend="max"
                          opacity={opacity}
                          lens
                          lensSize={180}
                          zoom={2.5}
                          zoomMode="lens"
                          lensContentOpacity={1}
                          lensSource="composite"
                          enableZoom
                        />
                      </Box>
                    )}

                    {/* 비어 있으면 원인을 보여준다. 조용히 빈 칸으로 두지 않는다. */}
                    {!heatmapLoading && !(scoreMapImage && imageData) && (
                      <Typography
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          px: "12px",
                          textAlign: "center",
                          whiteSpace: "pre-line",
                          wordBreak: "break-all",
                          fontSize: "12px",
                          fontFamily: SF,
                          color: "var(--dk-text-3, #aeaeb2)",
                        }}
                      >
                        {heatmapMsg ?? t("NoImage")}
                      </Typography>
                    )}

                    {heatmapLoading && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          backgroundColor: "var(--dk-surface, #fff)",
                          zIndex: 2,
                        }}
                      >
                        <CircularProgress size={40} />
                      </Box>
                    )}
                  </Box>
                )}

                {contourMode && (
                  // 컨테이너 전체를 덮으면 히트맵 칸까지 가린다. 원본 칸 위에만 깐다.
                  <Box
                    sx={{
                      gridArea: "1 / 1",
                      position: "relative",
                      overflow: "hidden",
                      zIndex: 3,
                      background: "var(--dk-surface, #fff)",
                    }}
                  >
                    <img
                      ref={contourImgRef}
                      src={imageSrc}
                      alt=""
                      onLoad={(e) =>
                        setNaturalSize({ w: e.currentTarget.naturalWidth, h: e.currentTarget.naturalHeight })
                      }
                      style={{ width: "100%", height: "100%", objectFit: "contain", display: "block" }}
                    />
                    {naturalSize && (
                      // viewBox + meet = objectFit: contain 과 같은 배치라 원본 좌표 그대로 그린다
                      <svg
                        viewBox={`0 0 ${naturalSize.w} ${naturalSize.h}`}
                        preserveAspectRatio="xMidYMid meet"
                        onClick={handleContourClick}
                        onContextMenu={handleContourRemove}
                        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", cursor: "crosshair" }}
                      >
                        {segments.map((s) =>
                          s.polygons.map((ring, i) => (
                            <polygon
                              key={`${s.id}-${i}`}
                              points={ring.join(" ")}
                              fill="rgba(255,59,48,0.25)"
                              stroke="#ff3b30"
                              strokeWidth={2}
                              vectorEffect="non-scaling-stroke"
                            >
                              <title>{s.label}</title>
                            </polygon>
                          )),
                        )}
                      </svg>
                    )}
                    {segmenting && (
                      <Box
                        sx={{
                          position: "absolute",
                          inset: 0,
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          pointerEvents: "none",
                        }}
                      >
                        <CircularProgress size={32} />
                      </Box>
                    )}
                  </Box>
                )}
              </Box>
            )}
          </>
        }
      />

      {/* Footer */}
      <Box
        sx={{
          px: "20px",
          py: "14px",
          borderTop: "0.5px solid var(--dk-border, rgba(0,0,0,0.07))",
          background: "var(--dk-surface-glass, rgba(255,255,255,0.4))",
          display: "flex",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Button
          id="make-dataset-btn"
          onClick={handleMakeDataSet}
          disabled={!filters.material}
          sx={{
            height: 32,
            px: "14px",
            borderRadius: "8px",
            fontSize: "12px",
            fontWeight: 500,
            textTransform: "none",
            fontFamily: SF,
            border: "0.5px solid var(--dk-border, rgba(0,0,0,0.14))",
            background: "var(--dk-surface, white)",
            color: "var(--dk-text, #1d1d1f)",
            boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
            "&:hover": { background: "var(--dk-surface-2, #f5f5f7)" },
            "&.Mui-disabled": { opacity: 0.4 },
          }}
        >
          {t("MakeDataSet")}
        </Button>

        <Box sx={{ display: "flex", alignItems: "center", gap: "10px" }}>
          <Typography sx={{ fontSize: "12px", color: "var(--dk-text-3, #aeaeb2)", fontFamily: SF }}>
            {t("PrevNextImage")}
          </Typography>
          <Box sx={{ display: "flex", gap: "5px" }}>
            {[
              { icon: <KeyboardArrowUpIcon fontSize="small" />, onClick: onPrevious },
              { icon: <KeyboardArrowDownIcon fontSize="small" />, onClick: onNext },
            ].map((btn, i) => (
              <IconButton
                key={i}
                onClick={btn.onClick}
                sx={{
                  width: 30,
                  height: 30,
                  borderRadius: "8px",
                  border: "0.5px solid var(--dk-border, rgba(0,0,0,0.14))",
                  background: "var(--dk-surface, white)",
                  boxShadow: "0 1px 2px rgba(0,0,0,0.06)",
                  "&:hover": { background: "var(--dk-surface-2, #f5f5f7)" },
                }}
              >
                {btn.icon}
              </IconButton>
            ))}
          </Box>
        </Box>
      </Box>
    </>
  );
}
